import { DAY_MS } from "./freshness.js";
import type { Exercise } from "./schemas/exercise.js";
import type { TimedSessionResult } from "./progressTypes.js";
import { catalogBySlug, resolveExerciseMuscleCredits } from "./volumeByMuscle.js";
import { HARD_SET_MUSCLE_CONTRIB_MIN } from "./weeklyTargetActual.js";

// PG-20: lo stato del carico, il nucleo che la card di Home (HM-3) e la riga
// per muscolo del Progress leggono INSIEME. Estratto da `homeFormBadge.ts`
// perché il secondo chiamante arriva con PG-20: una sola funzione, due letture.

/** Il minimo che il kernel legge di una sessione: quando e quante serie dure.
 *  `FormSession` di Home lo soddisfa così com'è. */
export type DecaySession = { startedAtMs: number; hardSets: number };

/**
 * Serie dure al giorno, pesate per quanto sono recenti: ogni sessione conta
 * `hardSets × 2^(-giorni/emivita)`.
 *
 * La somma pesata viene divisa per la **massa del kernel sui giorni davvero
 * osservati**, così il risultato è una VELOCITÀ (serie dure al giorno) e due
 * emivite diverse producono numeri sulla STESSA scala.
 *
 * 🪤 **Due normalizzazioni sbagliate, prima di questa, e le ha prese un test.**
 * `× k` è quella del continuo (`∫ r e^{-kt} dt = r/k`): con eventi giornalieri
 * lascia un bias `k/(1-e^{-k})` che cresce con `k`, e a ritmo costante l'acuto
 * leggeva 11,8 contro 10,25 del cronico. `× (1-e^{-k})` toglie quello ma non la
 * **coda troncata**: su una storia finita il cronico perde la sua parte lontana e
 * ogni utente NUOVO risultava in eccesso del 5-16%. ⛔ Entrambe avrebbero prodotto
 * un picco falso per costruzione, cioè lo stesso genere di difetto che questo
 * modulo ha appena tolto all'ACWR.
 * ⭐ Con la massa osservata: ritmo costante ⇒ eccesso **0.000**, e un utente con
 * tre settimane di storia ⇒ **+0.023**.
 *
 * ⛔ Pura: `nowMs` è iniettato, nessun `Date.now()` qui dentro.
 */
export function decayedHardSetRate(
  sessions: ReadonlyArray<DecaySession>,
  nowMs: number,
  halfLifeDays: number,
): number {
  const k = Math.LN2 / halfLifeDays;
  let sum = 0;
  let spanDays = 0;
  for (const s of sessions) {
    const days = (nowMs - s.startedAtMs) / DAY_MS;
    if (days < 0) continue; // una sessione nel futuro non è ancora successa
    sum += s.hardSets * Math.exp(-k * days);
    if (days > spanDays) spanDays = days;
  }
  if (sum === 0) return 0;
  // Σ e^{-k·d} sui giorni osservati (serie geometrica troncata allo span reale).
  const mass = (1 - Math.exp(-k * (Math.floor(spanDays) + 1))) / (1 - Math.exp(-k));
  return sum / mass;
}

// ─── PG-20: lo stato PER MUSCOLO ────────────────────────────────────────────
//
// Il secondo mestiere della riga del Progress (§8ter, sue decisioni del 15/09):
// quanto carico recente è ancora recente, per muscolo. Una quantità che DECADE
// e non si azzera mai, accanto alla banda settimanale e mai dentro di lei.
// ⛔ Niente soglie, niente zone, niente binario, niente parole di direzione:
// lo stato è un RAPPORTO fra due pesature della stessa storia, e una forma.
// ⛔ Non legge `computeFormBadge`: le sue soglie 0.25/0.5 sarebbero una banda
// derivata dai suoi dati, esattamente ciò che §8ter.2 vieta.
// ⚠️ La pesatura per prossimità al cedimento di §8ter.1 è LINEARE nel RIR, ma
// il `SessionResult` conserva solo il binario (`hardSetCount` = serie a RIR ≤ 3).
// Questo primo taglio legge quel binario, e lo dichiara: il lineare vuole un
// campo nuovo e un ricalcolo in produzione, che è `⚠︎` e si chiede prima.

// ⚠️ IPOTESI, non fisiologia. Sono le stesse emivite del badge di Home, per sua
// scelta del 25/09 («riusa 7/28, come ipotesi»), non per derivazione: la forma
// del decadimento è sostenuta, i numeri no. Stanno qui e non importati da Home
// perché si tarano a parte: tararne uno non deve spostare l'altro. La taratura
// sui suoi dati è il residuo dichiarato di `PG-20`
// (📖 next/measurement-contract.md §8ter, «Le emivite 7g/28g»).
export const MUSCLE_STATE_ACUTE_HALF_LIFE_DAYS = 7;
export const MUSCLE_STATE_CHRONIC_HALF_LIFE_DAYS = 28;
// Sessioni che hanno allenato QUESTO muscolo prima che il cronico voglia dire
// qualcosa. ⚠️ Nostro, a tavolino, come il gemello di Home: resta da tarare.
export const MUSCLE_STATE_MIN_SESSIONS = 4;
// La traccia copre sei settimane con UN punto a settimana, oggi compreso: 7
// punti, tutti nello stesso giorno della settimana di oggi. ⭐ Sua scelta del
// 25/09, dopo il banco: campionata ogni giorno la linea recente faceva un dente
// a ogni sessione (due a settimana), cioè disegnava il rumore di fase che il
// numero a passi di 5 esiste per non stampare. Stesso giorno della settimana ⇒
// stessa fase ⇒ il dente sparisce, e resta la forma.
export const MUSCLE_STATE_TRACE_WEEKS = 6;
// La precisione DICHIARATA del numero, in punti percentuali. A ritmo costante
// il rapporto oscilla di ±11% da solo (misurato, `homeFormBadge.ts`), e per
// muscolo di più: stampare -33 invece di -35 sarebbe stampare rumore. ⛔ Non è
// una soglia: non separa niente, arrotonda. Sua scelta del 25/09.
const MUSCLE_STATE_STEP_PCT = 5;

/** Il rapporto acuto/cronico - 1, in punti percentuali, a passi di 5.
 *  L'UNICO posto dove si arrotonda lo stato: la riga, l'etichetta a11y e i test
 *  leggono tutti questo numero. Mai `-0`. */
export const muscleStateStepPct = (ratio: number): number => {
  const v = Math.round((ratio * 100) / MUSCLE_STATE_STEP_PCT) * MUSCLE_STATE_STEP_PCT;
  return v === 0 ? 0 : v;
};

export type MuscleState =
  /** Nessuna serie dura registrata su questo muscolo. */
  | { kind: "none" }
  /** Qualche sessione, non abbastanza per un ritmo di fondo. */
  | { kind: "thin"; sessions: number; needed: number }
  | {
      kind: "pace";
      sessions: number;
      /** acuto / cronico - 1, oggi. Grezzo: si stampa via `stepPct`. */
      ratio: number;
      stepPct: number;
      /** Serie dure al giorno, un campione a settimana, dal più vecchio a oggi.
       *  Stessa scala per le due, così la traccia le mette sullo stesso asse. */
      acute: number[];
      chronic: number[];
    };

/** Una passata su tutte le sessioni, attribuzione per credito, parametrizzata
 *  su cosa contare (`countOf`). */
const sessionsByMuscle = <T extends { startedAtMs: number }>(
  sessions: ReadonlyArray<TimedSessionResult>,
  catalog: ReadonlyArray<Exercise>,
  index: ReadonlyMap<string, Exercise> | undefined,
  countOf: (er: TimedSessionResult["result"]["exerciseResults"][number]) => number,
  make: (startedAtMs: number, sets: number) => T,
): Map<string, T[]> => {
  const bySlug = index ?? catalogBySlug(catalog);
  const out = new Map<string, T[]>();
  for (const s of sessions) {
    const perMuscle: Record<string, number> = {};
    for (const er of s.result.exerciseResults) {
      const n = countOf(er);
      if (n <= 0) continue;
      const ex = bySlug.get(er.exerciseSlug);
      if (!ex) continue;
      for (const [m, credit] of resolveExerciseMuscleCredits(
        ex,
        HARD_SET_MUSCLE_CONTRIB_MIN,
      )) {
        perMuscle[m] = (perMuscle[m] ?? 0) + n * credit;
      }
    }
    for (const [m, sets] of Object.entries(perMuscle)) {
      if (sets <= 0) continue;
      const list = out.get(m);
      const entry = make(s.startedAtMs, sets);
      if (list) list.push(entry);
      else out.set(m, [entry]);
    }
  }
  return out;
};

/**
 * Le serie dure di ogni sessione, per muscolo: `muscolo → [{startedAtMs,
 * hardSets}]`. Stessa attribuzione di `hardSetsByMuscle` (credito pieno ai
 * motori principali, frazionario ai sinergici, via `resolveExerciseMuscleCredits`),
 * ma ⛔ NON la sua forma: quella SOMMA dentro una finestra, questa conserva la
 * storia sessione per sessione, perché il decadimento ha bisogno di sapere
 * QUANDO, non solo quanto. Una sola passata su tutte le sessioni.
 */
export const hardSetSessionsByMuscle = (
  sessions: ReadonlyArray<TimedSessionResult>,
  catalog: ReadonlyArray<Exercise>,
  index?: ReadonlyMap<string, Exercise>,
): Map<string, DecaySession[]> =>
  sessionsByMuscle(
    sessions,
    catalog,
    index,
    (er) => er.hardSetCount ?? 0,
    (startedAtMs, hardSets) => ({ startedAtMs, hardSets }),
  );

/** Una sessione di lavoro su un muscolo: tutte le serie di lavoro contate
 *  (`setCount`), dure o no. Non è `DecaySession`: `hardSets` mentirebbe. */
export type WorkSetSession = { startedAtMs: number; sets: number };

/**
 * Come `hardSetSessionsByMuscle` (stessa attribuzione, stessa passata) ma su
 * OGNI serie di lavoro contata. ⛔ Solo per l'ultimo lavoro del muscolo (#322):
 * un muscolo allenato con serie leggere si è comunque stancato. Stato, ritmo e
 * buco della settimana restano sulle serie dure.
 */
export const workSetSessionsByMuscle = (
  sessions: ReadonlyArray<TimedSessionResult>,
  catalog: ReadonlyArray<Exercise>,
  index?: ReadonlyMap<string, Exercise>,
): Map<string, WorkSetSession[]> =>
  sessionsByMuscle(
    sessions,
    catalog,
    index,
    (er) => er.setCount ?? 0,
    (startedAtMs, sets) => ({ startedAtMs, sets }),
  );

/**
 * Lo stato di un muscolo dalla sua storia. ⛔ Pura: `nowMs` è iniettato.
 * La banda non entra: un muscolo senza banda pubblicata ha lo stato come gli
 * altri, perché lo stato non dipende da una norma.
 */
export const muscleStateFrom = (
  history: ReadonlyArray<DecaySession>,
  nowMs: number,
): MuscleState => {
  // Una sessione nel futuro non è ancora successa, e non si conta.
  const past = history.filter((s) => s.startedAtMs <= nowMs);
  if (past.length === 0) return { kind: "none" };
  if (past.length < MUSCLE_STATE_MIN_SESSIONS) {
    return { kind: "thin", sessions: past.length, needed: MUSCLE_STATE_MIN_SESSIONS };
  }
  const acute: number[] = [];
  const chronic: number[] = [];
  for (let w = MUSCLE_STATE_TRACE_WEEKS; w >= 0; w--) {
    // Indietro di settimane di CALENDARIO (`setDate`), non di 7 × 24 h: una
    // settimana che contiene un cambio d'ora resta sullo stesso giorno e ora.
    const d = new Date(nowMs);
    d.setDate(d.getDate() - w * 7);
    const at = d.getTime();
    acute.push(decayedHardSetRate(past, at, MUSCLE_STATE_ACUTE_HALF_LIFE_DAYS));
    chronic.push(decayedHardSetRate(past, at, MUSCLE_STATE_CHRONIC_HALF_LIFE_DAYS));
  }
  const a = acute[acute.length - 1] ?? 0;
  const c = chronic[chronic.length - 1] ?? 0;
  // c > 0 qui: almeno una sessione passata con serie dure positive.
  const ratio = c > 0 ? a / c - 1 : 0;
  return {
    kind: "pace",
    sessions: past.length,
    ratio,
    stepPct: muscleStateStepPct(ratio),
    acute,
    chronic,
  };
};
